import type { Lang } from '@app/shared';
import { BUSINESS_STRINGS } from './dict.business';
import { CALENDAR_STRINGS } from './dict.calendar';
import { EXTRAS_STRINGS } from './dict.extras';
import { SETTINGS_STRINGS } from './dict.settings';

/**
 * Словарь интерфейса. Единственное место, где живут видимые строки (ТЗ §14.3:
 * «RU/UZ переключатель, все строки через i18n — включая тексты ошибок антиспама»).
 *
 * Форма `[ru, uz]` взята из прототипа `pronto_booking.html` намеренно: пара в одной строке
 * не даёт добавить русский ключ и забыть узбекский — отсутствие второго элемента ловит
 * компилятор, а не тестировщик через месяц. Тексты, которые уже есть в прототипе,
 * перенесены оттуда дословно: расхождение формулировок с эталоном — дефект.
 *
 * Ключи ошибок бэкенда (`err.*`) лежат ЗДЕСЬ ЖЕ, а не в отдельном словаре: их набор задан
 * `ApiErrorKey` из `@app/shared`, и разложить их по двум файлам значило бы завести два места,
 * где можно забыть новый ключ.
 */
export const UI_STRINGS = {
  /**
   * Строки кабинета живут в трёх отдельных файлах и вливаются сюда.
   *
   * Причина чисто практическая: над кабинетом работают параллельно, и один общий файл
   * строк давал бы конфликт на каждом коммите. Разрез по зонам ответственности —
   * `dict.business` (организация, онбординг, правила, тариф), `dict.calendar` (календарь
   * и статусы), `dict.settings` (услуги, специалисты, часы, отпуска).
   *
   * Тип `UiKey` собирается из результата склейки, поэтому новый ключ в любом из файлов
   * сразу доступен `t()` и по-прежнему проверяется компилятором. Дубль ключа между
   * файлами тихо перекроет предыдущий — держите префиксы разными (`cal*`, `srv*`, `biz*`).
   */
  ...BUSINESS_STRINGS,
  ...CALENDAR_STRINGS,
  ...SETTINGS_STRINGS,
  ...EXTRAS_STRINGS,

  // ── Общее ──
  brandTagline: [
    'Запись за 3 тапа — прямо в Telegram',
    '3 tap bilan yozuv — toʻgʻridan-toʻgʻri Telegramda',
  ],
  back: ['Назад', 'Orqaga'],
  close: ['Закрыть', 'Yopish'],
  retry: ['Повторить', 'Qayta urinish'],
  loading: ['Загрузка…', 'Yuklanmoqda…'],
  min: ['мин', 'daq'],
  sum: ['сум', 'soʻm'],
  addrNone: ['Адрес не указан', 'Manzil koʻrsatilmagan'],

  // ── Город и район (справочник @app/shared). Названия самих городов — данные, не строки i18n. ──
  geoAllCities: ['Все города', 'Barcha shaharlar'],
  geoPickCity: ['Выберите город', 'Shaharni tanlang'],
  geoPickDistrict: ['Выберите район', 'Tumanni tanlang'],
  geoAllDistricts: ['Все районы', 'Barcha tumanlar'],
  geoCity: ['Город', 'Shahar'],
  geoCityNone: ['Город не указан', 'Shahar koʻrsatilmagan'],

  // ── Нижняя навигация клиента ──
  navCatalog: ['Каталог', 'Katalog'],
  navMyBook: ['Мои записи', 'Yozuvlarim'],
  navProfile: ['Профиль', 'Profil'],

  // ── Профиль клиента ──
  profileTitle: ['Профиль', 'Profil'],
  profileLang: ['Язык', 'Til'],

  // ── Каталог ──
  catalogTitle: ['К кому хотите записаться?', 'Kimga yozilmoqchisiz?'],
  catalogSub: [
    'Найдите специалиста и выберите удобное время',
    'Mutaxassisni toping va qulay vaqtni tanlang',
  ],
  catalogSearchPh: ['Поиск специалиста или услуги', 'Mutaxassis yoki xizmat qidirish'],
  catalogEmpty: [
    'Ничего не нашлось. Попробуйте другую отрасль или запрос.',
    'Hech narsa topilmadi. Boshqa soha yoki soʻrovni sinab koʻring.',
  ],
  loadMore: ['Показать ещё', 'Yana koʻrsatish'],

  // ── Страница организации ──
  bizServices: ['Услуги и цены', 'Xizmatlar va narxlar'],
  ctaBook: ['Записаться', 'Yozilish'],
  emptyServices: [
    'У этой организации пока нет активных услуг.',
    'Bu tashkilotda faol xizmatlar hali yoʻq.',
  ],
  emptyStaff: ['Специалистов пока нет.', 'Mutaxassislar hali yoʻq.'],
  /* Оценок в публичном API сегодня нет (см. отчёт): блок рейтинга не рисуется вовсе,
     а не показывает «★ 0.0» — ноль читается как худшая оценка, а не как её отсутствие. */

  // ── Запись ──
  bookTitle: ['Онлайн-запись', 'Onlayn yozuv'],
  stepService: ['1 · Услуга', '1 · Xizmat'],
  /* У шага 2 подписи в словаре нет намеренно: заголовок отраслевой и собирается как
     '2 · ' + термин пресета — у стоматологии «2 · Врач», у репетитора «2 · Преподаватель». */
  stepSpecialistPrefix: ['2 · ', '2 · '],
  stepDay: ['3 · День', '3 · Kun'],
  stepTime: ['4 · Время', '4 · Vaqt'],
  pickTime: ['Выберите время', 'Vaqtni tanlang'],
  slotsEmpty: [
    'На этот день свободного времени нет. Выберите другой день.',
    'Bu kunda boʻsh vaqt yoʻq. Boshqa kunni tanlang.',
  ],
  bookedT: ['Вы записаны', 'Siz yozildingiz'],
  bookedB: [
    'Запись создана. Напомним в Telegram за сутки и за 2 часа до визита.',
    'Yozuv yaratildi. Tashrifdan bir kun va 2 soat oldin Telegramda eslatamiz.',
  ],
  goToMyBookings: ['К моим записям', 'Yozuvlarimga'],

  // ── Мои записи ──
  myBookTitle: ['Мои записи', 'Mening yozuvlarim'],
  myBookSub: ['Клиент', 'Mijoz'],
  upcoming: ['Предстоящие', 'Kelgusi'],
  past: ['Прошедшие', 'Oʻtgan'],
  myBookEmpty: [
    'Записей пока нет. Выберите организацию в каталоге.',
    'Yozuvlar hali yoʻq. Katalogdan tashkilotni tanlang.',
  ],
  upcomingEmpty: ['Предстоящих записей нет.', 'Kelgusi yozuvlar yoʻq.'],
  pastEmpty: ['Прошедших записей нет.', 'Oʻtgan yozuvlar yoʻq.'],
  tagNew: ['Ждёт', 'Kutilmoqda'],
  tagOk: ['Подтверждена', 'Tasdiqlangan'],
  tagDone: ['Завершена', 'Yakunlandi'],
  tagCancelled: ['Отменена', 'Bekor qilingan'],
  tagNoShow: ['Неявка', 'Kelmadi'],
  bookAgain: ['Повторить', 'Takrorlash'],
  confirmBooking: ['Подтвердить', 'Tasdiqlash'],
  cancelBooking: ['Отменить', 'Bekor qilish'],
  cancelLate: ['Отменить (поздняя отмена)', 'Bekor qilish (kech)'],
  safeCancelNote: [
    '✓ Отмена без последствий до {time}',
    '✓ {time} gacha oqibatsiz bekor qilish mumkin',
  ],
  lateCancelNote: [
    '⚠ Окно безопасной отмены закрылось в {time}',
    '⚠ Xavfsiz bekor qilish oynasi {time} da yopildi',
  ],
  /* Срок безопасной отмены приходит с сервера. Если его нет, экран говорит об этом вслух:
     подставить окно из пресета отрасли нельзя (AGENTS.md, правило 5 — пресет копируется,
     а не читается), а промолчать — значит выдать позднюю отмену за безопасную. */
  reminderNote: [
    '🔔 Напомним в Telegram за день и за 2 часа',
    '🔔 Telegramda bir kun va 2 soat oldin eslatamiz',
  ],
  cancelSheetT: ['Отменить запись?', 'Yozuvni bekor qilasizmi?'],
  cancelSheetSafe: [
    'Отмена внутри безопасного окна последствий не имеет. Слот сразу освободится для других.',
    'Xavfsiz oyna ichida bekor qilish oqibatga olib kelmaydi. Slot darhol boshqalarga boʻshaydi.',
  ],
  cancelSheetLate: [
    'Окно безопасной отмены уже закрылось. Санкций не будет, но организация увидит пометку «поздняя отмена».',
    'Xavfsiz bekor qilish oynasi yopilgan. Jazo boʻlmaydi, lekin tashkilot «kech bekor qilish» belgisini koʻradi.',
  ],
  cancelKeep: ['Оставить', 'Qoldirish'],
  cancelledLateT: ['Запись отменена как поздняя', 'Yozuv kech bekor qilindi'],
  cancelledLateB: [
    'Слот освобождён. Пометка «поздняя отмена» видна организации, но ограничений не влечёт.',
    'Slot boʻshadi. «Kech bekor qilish» belgisi tashkilotga koʻrinadi, lekin cheklov keltirmaydi.',
  ],
  cancelledT: ['Запись отменена', 'Yozuv bekor qilindi'],
  cancelledB: ['Слот освобождён.', 'Slot boʻshadi.'],

  // ── Предупреждение о неявке (ТЗ §14.2) ──
  nsWarnT: ['У вас зафиксирована неявка', 'Sizda kelmaslik qayd etildi'],
  nsWarnB: [
    'Ещё две — и запись будет ограничена. Если планы изменились, отменяйте заранее.',
    'Yana ikkitasi — va yozilish cheklanadi. Rejalar oʻzgarsa, oldindan bekor qiling.',
  ],
  nsWarnManyT: ['У вас несколько неявок', 'Sizda bir nechta kelmaslik bor'],
  nsWarnManyB: [
    'Зафиксировано неявок: {n}. Следующая может закрыть запись по всей платформе.',
    'Qayd etilgan kelmasliklar: {n}. Keyingisi butun platformada yozilishni yopishi mumkin.',
  ],
  nsWarnLink: ['Как работают ограничения →', 'Cheklovlar qanday ishlaydi →'],

  // ── «Запись недоступна» (ТЗ §14.2) ──
  banTitle: ['Запись недоступна', 'Yozuv mavjud emas'],
  banSub: ['Ограничения аккаунта', 'Hisob cheklovlari'],
  banLocalT: ['Локальное ограничение', 'Mahalliy cheklov'],
  banLocalB: [
    'Запись в «{biz}» недоступна до {date}. Причина — две неявки в этом заведении.',
    '«{biz}»ga yozilish {date}gacha mavjud emas. Sabab — shu muassasada ikki marta kelmaslik.',
  ],
  banLocalForever: [
    'Запись в «{biz}» закрыта бессрочно. Причина — две неявки в этом заведении. Снять ограничение может только само заведение.',
    '«{biz}»ga yozilish muddatsiz yopilgan. Sabab — shu muassasada ikki marta kelmaslik. Cheklovni faqat muassasaning oʻzi olib tashlashi mumkin.',
  ],
  banGlobalT: ['Глобальное ограничение', 'Global cheklov'],
  banGlobalB: [
    'Аккаунт ограничен до {date} из-за повторных неявок. Новые записи создать нельзя.',
    'Takroriy kelmasliklar sababli hisob {date}gacha cheklangan. Yangi yozuv yaratib boʻlmaydi.',
  ],
  banGlobalForever: [
    'Аккаунт ограничен бессрочно из-за повторных неявок. Новые записи создать нельзя.',
    'Takroriy kelmasliklar sababli hisob muddatsiz cheklangan. Yangi yozuv yaratib boʻlmaydi.',
  ],
  banRulesT: ['Как работают правила', 'Qoidalar qanday ishlaydi'],
  banRule1: [
    'Первая неявка — только предупреждение, без ограничений.',
    'Birinchi kelmaslik — faqat ogohlantirish, cheklovsiz.',
  ],
  banRule2: [
    'Вторая неявка в одном заведении за 90 дней — запись туда закрыта на срок, который задаёт заведение.',
    'Bitta muassasada 90 kun ichida ikkinchi kelmaslik — u yerga yozilish muassasa belgilagan muddatga yopiladi.',
  ],
  /* Решение 11.10.2026 (ТЗ §19 №18): глобальный бан требует неявок минимум у двух РАЗНЫХ
     заведений. Клиенту это важно знать именно здесь: иначе три отметки от одного владельца
     выглядят как угроза всему аккаунту, хотя закрывают запись только к нему. */
  banRule3: [
    'Третья неявка за 90 дней, минимум в двух разных заведениях — аккаунт ограничен на 90 дней. Отметки одного заведения закрывают запись только к нему.',
    'Kamida ikkita turli muassasada 90 kun ichida uchinchi kelmaslik — hisob 90 kunga cheklanadi. Bitta muassasaning belgilari faqat oʻsha yerga yozilishni yopadi.',
  ],
  banRule4: [
    'Отмена внутри безопасного окна последствий не имеет.',
    'Xavfsiz oyna ichida bekor qilish oqibatga olib kelmaydi.',
  ],
  /* Решение 22.09.2026 (ТЗ §19 №16): окно подсчёта неявок — 90 дней. Клиент должен знать,
     что старые неявки «сгорают» и после ограничения счёт начинается заново. */
  banRule5: [
    'Считаются только неявки за последние 90 дней: более старые не учитываются. Поэтому после окончания ограничения счёт начинается с нуля — но новые неявки снова приведут к ограничению.',
    'Faqat soʻnggi 90 kundagi kelmasliklar hisoblanadi: eskilari hisobga olinmaydi. Shuning uchun cheklov tugagach hisob noldan boshlanadi — lekin yangi kelmasliklar yana cheklovga olib keladi.',
  ],
  banHelp: [
    'Если отметка поставлена по ошибке, заведение может её снять — ограничение исчезнет сразу.',
    'Agar belgi xato qoʻyilgan boʻlsa, muassasa uni olib tashlashi mumkin — cheklov darhol yoʻqoladi.',
  ],
  banKeep: [
    'Уже созданные записи остаются в силе — ограничение блокирует только новые.',
    'Allaqachon yaratilgan yozuvlar kuchda qoladi — cheklov faqat yangilarini toʻsadi.',
  ],

  // ── Термины специалиста по отраслям (ТЗ §3). Строчные: заглавную ставит cap(). ──
  termUsta: ['мастер', 'usta'],
  termDoc: ['врач', 'shifokor'],
  termMass: ['массажист', 'massajchi'],
  termTeach: ['преподаватель', 'oʻqituvchi'],
  termGroom: ['грумер', 'grumer'],

  // ── Вход (ТЗ §5) ──
  /* Показывается ТОЛЬКО когда подпись есть, а сервер аккаунт не завёл. Вне Telegram
     подписи нет вовсе, и это нормальное состояние: там плашки не будет (см. api/session.ts). */
  sessionFailedT: ['Вход не выполнен', 'Kirish amalga oshmadi'],
  sessionFailedB: [
    'Каталог открыт, но записаться и посмотреть «Мои записи» сейчас не получится.',
    'Katalog ochiq, lekin hozir yozilish va «Yozuvlarim»ni koʻrish imkoni yoʻq.',
  ],

  // ── Ошибки API. Ключи задаются `ApiErrorKey` из @app/shared. ──
  errTitle: ['Не получилось', 'Boʻlmadi'],
  'err.globallyBanned': [
    'Аккаунт ограничен из-за повторных неявок. Новые записи создать нельзя.',
    'Takroriy kelmasliklar sababli hisob cheklangan. Yangi yozuv yaratib boʻlmaydi.',
  ],
  'err.blacklisted': [
    'Эта организация закрыла вам запись после двух неявок.',
    'Bu tashkilot ikki marta kelmaganingizdan soʻng yozilishni yopdi.',
  ],
  'err.tooManyActive': [
    'Уже есть {limit} активных записей. Отмените одну из них, чтобы создать новую.',
    'Allaqachon {limit} ta faol yozuv bor. Yangisini yaratish uchun bittasini bekor qiling.',
  ],
  'err.cooldown': [
    'Слишком часто. Попробуйте снова через {minutes} мин.',
    'Juda tez-tez. {minutes} daqiqadan soʻng qayta urining.',
  ],
  'err.slotTaken': [
    'Это время только что заняли. Выберите другое.',
    'Bu vaqt hozirgina band qilindi. Boshqasini tanlang.',
  ],
  'err.unauthorized': [
    'Откройте приложение из Telegram — без этого мы не знаем, кто вы.',
    'Ilovani Telegramdan oching — busiz sizni taniy olmaymiz.',
  ],
  'err.initDataInvalid': [
    'Подпись Telegram не прошла проверку. Перезапустите приложение из чата с ботом.',
    'Telegram imzosi tekshiruvdan oʻtmadi. Ilovani bot bilan chatdan qayta oching.',
  ],
  'err.initDataExpired': [
    'Сессия Telegram устарела. Перезапустите приложение.',
    'Telegram sessiyasi eskirdi. Ilovani qayta oching.',
  ],
  'err.forbidden': ['Действие недоступно.', 'Amal mavjud emas.'],
  'err.notFound': ['Не найдено.', 'Topilmadi.'],
  'err.validation': [
    'Данные не приняты сервером (поле «{field}»).',
    'Server maʼlumotlarni qabul qilmadi («{field}» maydoni).',
  ],
  'err.rateLimited': [
    'Слишком много запросов. Подождите минуту.',
    'Soʻrovlar juda koʻp. Bir daqiqa kuting.',
  ],
  'err.slotOutsideWorkingHours': [
    'Это время вне часов работы организации.',
    'Bu vaqt tashkilotning ish vaqtidan tashqarida.',
  ],
  'err.slotInPast': ['Это время уже прошло.', 'Bu vaqt allaqachon oʻtdi.'],
  'err.businessNotPublished': [
    'Организация сейчас не принимает записи.',
    'Tashkilot hozir yozuv qabul qilmayapti.',
  ],
  'err.publishRequiresService': [
    'Нельзя опубликовать организацию без активной услуги.',
    'Faol xizmatsiz tashkilotni chop etib boʻlmaydi.',
  ],
  'err.publishRequiresStaff': [
    'Нельзя опубликовать организацию без активного специалиста.',
    'Faol mutaxassissiz tashkilotni chop etib boʻlmaydi.',
  ],
  'err.workingHoursInvalid': [
    'Интервал расписания задан неверно.',
    'Jadval oraligʻi notoʻgʻri berilgan.',
  ],
  'err.workingHoursOverlap': [
    'Интервалы одного дня накладываются друг на друга.',
    'Bir kunning oraliqlari bir-birining ustiga tushmoqda.',
  ],
  /* Архив организации. Узбекский текст требует проверки носителем. */
  'err.businessHasActiveAppointments': [
    'Сначала отмените будущие записи.',
    'Avval kelgusi yozuvlarni bekor qiling.',
  ],
  'err.businessArchived': [
    'Организация в истории. Сначала восстановите её.',
    'Tashkilot tarixda. Avval uni tiklang.',
  ],
  /* Защита клиента от преждевременных и «качающихся» отметок (ТЗ §19, №19 и №20).
     Узбекский текст требует проверки носителем. */
  'err.noShowTooEarly': [
    'Визит ещё не закончился. Отметить неявку можно после его окончания.',
    'Tashrif hali tugamadi. Kelmaganlikni u tugagandan keyin belgilash mumkin.',
  ],
  'err.noShowAlreadyForgiven': [
    'Отметку по этой записи уже снимали. Вернуть её нельзя.',
    'Bu yozuvdagi belgi allaqachon olib tashlangan. Uni qaytarib boʻlmaydi.',
  ],
  /* Возражение клиента «Я приходил» (ТЗ §19, №31). Узбекский требует проверки носителем. */
  'err.noShowAlreadyDisputed': [
    'Вы уже сообщили об этом. Заведение получило ваше сообщение.',
    'Siz bu haqda allaqachon xabar berdingiz. Muassasa xabaringizni oldi.',
  ],
  'err.noShowWrongStatus': [
    'Запись отменена или уже закрыта — неявку по ней не отмечают.',
    'Yozuv bekor qilingan yoki allaqachon yopilgan — unga kelmaganlik belgilanmaydi.',
  ],
  /* Ранний приход (ТЗ §19, №28). Узбекский требует проверки носителем. */
  calEarlyTitle: ['Визит ещё не начался', 'Tashrif hali boshlanmadi'],
  calEarlyBody: [
    'Клиент уже пришёл? Отметка закроет запись: оценить визит он сможет сразу, а отметить неявку будет уже нельзя.',
    'Mijoz keldimi? Belgi yozuvni yopadi: u tashrifni darhol baholay oladi, kelmaganlikni esa belgilab boʻlmaydi.',
  ],
  calEarlyYes: ['Да, пришёл', 'Ha, keldi'],
  'err.completeTooEarly': [
    'Визит ещё не начался. Отметить приход можно, но подтвердите, что клиент уже пришёл.',
    'Tashrif hali boshlanmadi. Kelganini belgilash mumkin, lekin mijoz kelganini tasdiqlang.',
  ],
  'err.proRequired': [
    'Возможность доступна на платной подписке.',
    'Bu imkoniyat pullik obunada mavjud.',
  ],
  'err.localBanExpired': ['Ограничение уже не действует.', 'Cheklov endi amal qilmaydi.'],
  /* Отсутствия: раньше оба случая приходили как «неверный запрос», и владелец видел
     два своих поля, не зная, какое исправлять (ТЗ §19, №25). */
  'err.timeOffRangeInvalid': [
    'Конец отсутствия должен быть позже начала.',
    'Yoʻqlik tugashi boshlanishidan keyin boʻlishi kerak.',
  ],
  'err.timeOffInPast': [
    'Это время уже прошло. Отсутствие закрывает только будущие даты.',
    'Bu vaqt allaqachon oʻtgan. Yoʻqlik faqat kelgusi sanalarni yopadi.',
  ],
  'err.internal': [
    'Сервер не справился с запросом. Попробуйте ещё раз.',
    'Server soʻrovni bajara olmadi. Qayta urinib koʻring.',
  ],

  // ── Отказы, которые придумывает сам фронтенд: у бэкенда таких ключей нет ──
  'err.network': [
    'Сервер не отвечает. Проверьте связь и попробуйте ещё раз.',
    'Server javob bermayapti. Aloqani tekshirib, qayta urining.',
  ],
  'err.unparseable': [
    'Сервер ответил в неизвестном формате (HTTP {status}).',
    'Server nomaʼlum formatda javob berdi (HTTP {status}).',
  ],
  'err.noInitData': [
    'Приложение запущено вне Telegram: подписать запрос нечем. Откройте его из чата с ботом.',
    'Ilova Telegramdan tashqarida ishga tushdi: soʻrovni imzolash imkoni yoʻq. Uni bot bilan chatdan oching.',
  ],
  /* Неизвестный ключ — видимая ошибка, а не «что-то пошло не так» без следа
     (AGENTS.md, правило 7). Сам ключ выводится в текст: по нему разбирают инцидент. */
  errUnknownKey: [
    'Сервер вернул неизвестную ошибку: {key}',
    'Server nomaʼlum xatolik qaytardi: {key}',
  ],
  /* Ключ известен, а подробностей для подстановки нет. Тоже видимая ошибка: без числа
     фраза «уже есть N активных записей» не говорит клиенту, сколько именно отменить. */
  errMissingDetails: [
    'Сервер вернул ошибку {key} без нужных подробностей.',
    'Server {key} xatoligini kerakli tafsilotlarsiz qaytardi.',
  ],
} as const satisfies Record<string, readonly [string, string]>;

export type UiKey = keyof typeof UI_STRINGS;

const LANG_INDEX: Record<Lang, 0 | 1> = { ru: 0, uz: 1 };

/** Строка по ключу. Ключи типизированы, поэтому промах по имени ловит компилятор. */
export function translate(lang: Lang, key: UiKey): string {
  return UI_STRINGS[key][LANG_INDEX[lang]];
}

const PLACEHOLDER = /\{([a-zA-Z][a-zA-Z0-9]*)\}/;

/**
 * Подстановка `{name}` в шаблон.
 *
 * Незаполненный плейсхолдер — ОШИБКА, а не «оставим как есть»: строка
 * «Отмена без последствий до {time}» на экране клиента выглядит как сломанное приложение,
 * и молча выпустить её хуже, чем упасть на месте вызова, где дефект виден сразу.
 */
export function fill(template: string, params: Record<string, string | number>): string {
  let result = template;
  for (const [name, value] of Object.entries(params)) {
    result = result.split(`{${name}}`).join(String(value));
  }
  const missed = PLACEHOLDER.exec(result);
  if (missed) {
    throw new Error(`Не заполнен плейсхолдер {${missed[1]}} в строке: ${template}`);
  }
  return result;
}

/** Строка с подстановками. */
export function translateWith(
  lang: Lang,
  key: UiKey,
  params: Record<string, string | number>,
): string {
  return fill(translate(lang, key), params);
}

/** Первая буква заглавная. Нужна отраслевым терминам: они хранятся строчными. */
export function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
